import type { ApiClientCore } from './apiClient.core.js';
import type { ApiResponse } from './apiClient.types.js';

export const outboxMethods = {
  /**
   * Get outbox processing errors (SUPER_DEV only, dev dashboard)
   */
  async getOutboxErrors(
    this: ApiClientCore,
    filters?: {
      tenantId?: number;
      tenantCode?: string;
      bankAccountId?: string;
      eventType?: string;
      since?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<ApiResponse<{
    errors: Array<{
      id: number;
      outboxEventId: number;
      tenantId: number;
      eventType: string;
      errorMessage: string;
      errorCode: string | null;
      retryCount: number;
      phase: string;
      metadata: Record<string, unknown> | null;
      occurredAt: string;
    }>;
    total: number;
  }>> {
    const params = new URLSearchParams();
    if (filters?.tenantId !== undefined) {
      params.set('tenantId', String(filters.tenantId));
    }
    if (filters?.tenantCode) {
      params.set('tenantCode', filters.tenantCode);
    }
    if (filters?.bankAccountId) {
      params.set('bankAccountId', filters.bankAccountId);
    }
    if (filters?.eventType) {
      params.set('eventType', filters.eventType);
    }
    if (filters?.since) {
      params.set('since', filters.since);
    }
    if (filters?.limit !== undefined) {
      params.set('limit', String(filters.limit));
    }
    if (filters?.offset !== undefined) {
      params.set('offset', String(filters.offset));
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.get(`/api/v1/dev/outbox/errors${query}`, true);
  },

  /**
   * List outbox operations for dev ops UI (SUPER_DEV only).
   */
  async getOutboxOperations(
    this: ApiClientCore,
    filters?: {
      tenantId?: number;
      tenantCode?: string;
      bankAccountId?: string;
      eventType?: string;
      since?: string;
      status?: string;
      onlyNonStandard?: boolean;
      limit?: number;
      offset?: number;
    }
  ): Promise<ApiResponse<{
    meta: { maxRetriesDefault: number; backoffScheduleMs: number[] };
    operations: Array<{
      outboxEvent: {
        id: number;
        eventType: string;
        status: string;
        retryCount: number;
        maxRetries: number;
        lastError: string | null;
        transactionId: number | null;
        tenantId: number | null;
        createdAt: string;
        sentAt: string | null;
      };
      retryTimeline: Array<{
        attempt: number;
        phase: 'retry' | 'final';
        errorMessage: string;
        occurredAt: string;
      }>;
      delivery: {
        status: string;
        messageId: string | null;
        failureReason: string | null;
      } | null;
      deliverySource: 'brevo' | 'fakturoid' | 'unavailable';
      providerAccepted: boolean;
      deliveryConfirmed: boolean;
      lateDeliveryRecovery?: boolean;
      resolution: string;
      terminalAlertLevel?: 'none' | 'warning' | 'critical';
      semanticState?:
        | 'PENDING'
        | 'PROCESSING'
        | 'FAILED_RETRYABLE'
        | 'TERMINAL'
        | 'DLQ'
        | 'SENT'
        | 'ABANDONED';
    }>;
    total: number;
  }>> {
    const params = new URLSearchParams();
    if (filters?.tenantId !== undefined) {
      params.set('tenantId', String(filters.tenantId));
    }
    if (filters?.tenantCode) {
      params.set('tenantCode', filters.tenantCode);
    }
    if (filters?.bankAccountId) {
      params.set('bankAccountId', filters.bankAccountId);
    }
    if (filters?.eventType) {
      params.set('eventType', filters.eventType);
    }
    if (filters?.since) {
      params.set('since', filters.since);
    }
    if (filters?.status) {
      params.set('status', filters.status);
    }
    if (filters?.onlyNonStandard !== undefined) {
      params.set('onlyNonStandard', filters.onlyNonStandard ? 'true' : 'false');
    }
    if (filters?.limit !== undefined) {
      params.set('limit', String(filters.limit));
    }
    if (filters?.offset !== undefined) {
      params.set('offset', String(filters.offset));
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.get(`/api/v1/dev/outbox/operations${query}`, true);
  },

  async getDevSuccessIncidentAttentionQueueSummary(
    this: ApiClientCore,
    filters?: {
      tenantCode?: string;
      bankAccountId?: string;
    }
  ): Promise<
    ApiResponse<{
      activeTotal: number;
      resolvedTotal: number;
      activeCountsByPriority: Record<'P0' | 'P1' | 'P2' | 'P3' | 'P4', number>;
      cachedAt: string;
      cacheTtlSeconds: number;
      platformIncidents?: Array<{
        id: 'subscription_payment_unavailable';
        severity: 'critical';
        priority: 'P0';
      }>;
    }>
  > {
    const params = new URLSearchParams();
    if (filters?.tenantCode) {
      params.set('tenantCode', filters.tenantCode);
    }
    if (filters?.bankAccountId) {
      params.set('bankAccountId', filters.bankAccountId);
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.get(`/api/v1/dev/success-incident/attention-queue/summary${query}`, true);
  },

  async getDevSuccessIncidentAttentionQueue(
    this: ApiClientCore,
    filters?: {
      tenantCode?: string;
      priority?: 'P0' | 'P1' | 'P2' | 'P3' | 'P4';
      overallHealth?: string;
      paymentId?: string;
      bankAccountId?: string;
      transactionId?: number;
      queueState?: 'active' | 'resolved';
      limit?: number;
      offset?: number;
    }
  ): Promise<ApiResponse<{
    items: Array<{
      transactionId: number;
      paymentId: string;
      overallHealth: string;
      priority: string;
    }>;
    total: number;
    platformIncidents?: Array<{
      id: 'subscription_payment_unavailable';
      severity: 'critical';
      priority: 'P0';
    }>;
  }>> {
    const params = new URLSearchParams();
    if (filters?.tenantCode) {
      params.set('tenantCode', filters.tenantCode);
    }
    if (filters?.priority) {
      params.set('priority', filters.priority);
    }
    if (filters?.overallHealth) {
      params.set('overallHealth', filters.overallHealth);
    }
    if (filters?.paymentId) {
      params.set('paymentId', filters.paymentId);
    }
    if (filters?.bankAccountId) {
      params.set('bankAccountId', filters.bankAccountId);
    }
    if (filters?.transactionId !== undefined) {
      params.set('transactionId', String(filters.transactionId));
    }
    if (filters?.queueState !== undefined) {
      params.set('queueState', filters.queueState);
    }
    if (filters?.limit !== undefined) {
      params.set('limit', String(filters.limit));
    }
    if (filters?.offset !== undefined) {
      params.set('offset', String(filters.offset));
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.get(`/api/v1/dev/success-incident/attention-queue${query}`, true);
  },

  async resolveDevSuccessIncidentAttention(
    this: ApiClientCore,
    transactionId: number,
    input?: { reason?: string; tenantCode?: string }
  ): Promise<ApiResponse<{ transactionId: number; created: boolean }>> {
    return this.post(
      `/api/v1/dev/success-incident/transactions/${transactionId}/resolve`,
      input ?? {},
      true
    );
  },

  async reopenDevSuccessIncidentAttention(
    this: ApiClientCore,
    transactionId: number,
    input?: { reason?: string; tenantCode?: string }
  ): Promise<ApiResponse<{ transactionId: number; created: boolean }>> {
    return this.post(
      `/api/v1/dev/success-incident/transactions/${transactionId}/reopen`,
      input ?? {},
      true
    );
  },

  async getDevSuccessIncidentTransactionDetail(
    this: ApiClientCore,
    transactionId: number,
    filters?: { tenantCode?: string }
  ): Promise<ApiResponse<{
    transactionId: number;
    paymentId: string;
    flow: string;
    handoffStatus: string;
    overallHealth: string;
    priority: string;
    suggestedOperatorAction?: string;
    obligations: Array<{
      type: string;
      status: string;
      blocksCustomer: boolean;
      operatorFollowUp: boolean;
      dueAt: string;
    }>;
    linkedOutboxEvents: Array<{
      id: number;
      eventType: string;
      semanticState: string;
      lastError: string | null;
    }>;
  }>> {
    const params = new URLSearchParams();
    if (filters?.tenantCode) {
      params.set('tenantCode', filters.tenantCode);
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.get(`/api/v1/dev/success-incident/transactions/${transactionId}${query}`, true);
  },

  /**
   * Retry a FAILED outbox event (SUPER_DEV only, dev dashboard)
   * Resets the event to PENDING for the worker to reprocess.
   */
  async retryOutboxEvent(
    this: ApiClientCore,
    outboxEventId: number,
    reason: string
  ): Promise<ApiResponse<{
    success: true;
    message: string;
  }>> {
    return this.post(
      `/api/v1/admin/outbox/events/${outboxEventId}/retry`,
      { reason },
      true
    );
  },

  async abandonOutboxEvent(this: ApiClientCore, outboxEventId: number, reason: string): Promise<ApiResponse<{
    accepted: boolean;
    message: string;
  }>> {
    return this.post(
      `/api/v1/admin/outbox/events/${outboxEventId}/abandon`,
      { reason },
      true
    );
  },

  async dryRunOutboxReplay(
    this: ApiClientCore,
    body: {
      tenantCodes: string[];
      selector: {
        semanticStates?: Array<'DLQ' | 'TERMINAL'>;
        eventTypes?: string[];
        from?: string;
        to?: string;
      };
      maxBatchSize: number;
    }
  ): Promise<ApiResponse<{
    candidateCount: number;
    eligibleCount: number;
    skipped: Array<{ eventId?: number; reason: string }>;
    estimatedBatches: number;
  }>> {
    return this.post('/api/v1/admin/outbox/replay/dry-run', body, true);
  },

  async createOutboxReplayApprovalRequest(
    this: ApiClientCore,
    body: {
      scope: 'tenant' | 'crossTenant';
      tenantCodes?: string[];
      selector: {
        semanticStates?: Array<'DLQ' | 'TERMINAL'>;
        eventTypes?: string[];
        from?: string;
        to?: string;
      };
      reason: string;
      idempotencyKey: string;
      ttlMinutes: number;
    }
  ): Promise<ApiResponse<{ approvalRequestId: string; status: string; expiresAt: string }>> {
    return this.post('/api/v1/admin/outbox/replay/approval-requests', body, true);
  },

  async approveOutboxReplayRequest(
    this: ApiClientCore,
    approvalRequestId: string,
    reason: string
  ): Promise<ApiResponse<{ approvalRequestId: string; status: string; approvedBy: number[] }>> {
    return this.post(`/api/v1/admin/outbox/replay/approval-requests/${encodeURIComponent(approvalRequestId)}/approve`, { reason }, true);
  },

  async revokeOutboxReplayRequest(
    this: ApiClientCore,
    approvalRequestId: string,
    reason: string
  ): Promise<ApiResponse<{ approvalRequestId: string; status: string; revokedAt: string }>> {
    return this.post(`/api/v1/admin/outbox/replay/approval-requests/${encodeURIComponent(approvalRequestId)}/revoke`, { reason }, true);
  },

  async executeOutboxReplay(
    this: ApiClientCore,
    body: {
      approvalRequestId: string;
      maxBatchSize: number;
      idempotencyKey: string;
    }
  ): Promise<ApiResponse<{ accepted: true; approvalRequestId: string; status: string; enqueuedCount: number }>> {
    return this.post('/api/v1/admin/outbox/replay/execute', body, true);
  },
};
